import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';

import DeleteIconBordered from '../../../../components/svgs/DeleteIconBordered';
import { FHButton } from '../../../../components/base';
import FProgressBar from '../../../../components/base/FProgressBar';
import { FAutocomplete, FBoxContainer, FInput } from '../../../../components/baseV2';
import { FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../../utils/colors';
import { Themes } from '../../../../constants';
import { ConfirmDialog } from '../confirmDialog';
import { useFormReadOnly } from '../formReadOnlyProvider';
import { LIST_CONTAINER_SX, LIST_ROW_BG_SX, LIST_ROW_BORDER_SX } from '../../styles';
import { SELECTION_LIST_ERROR_BUTTON_SX } from './styles';

import type { FAutocompleteOption } from '../../../../components/baseV2';
import type { SelectionEntry, SelectionListProps } from './types';

export default function SelectionList({
  label,
  addButtonLabel,
  placeholder,
  options = [],
  freeText = false,
  entries,
  disabled: disabledProp = false,
  isSaving = false,
  required = false,
  error = false,
  addButtonSx,
  getDeleteConfirm,
  onAdd,
  onDelete,
}: SelectionListProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const readOnlyFromContext = useFormReadOnly();
  const disabled = disabledProp || readOnlyFromContext;
  const [pendingOption, setPendingOption] = useState<FAutocompleteOption | null>(null);
  const [pendingText, setPendingText] = useState('');
  const [pendingDeleteEntry, setPendingDeleteEntry] = useState<SelectionEntry | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const addingRef = useRef(false);
  const addInProgress = isSaving || isAdding;

  const optionsByValue = useMemo(() => {
    const map = new Map<string, FAutocompleteOption>();
    options.forEach((option) => map.set(option.value, option));
    return map;
  }, [options]);

  const selectedValues = useMemo(() => entries.map((entry) => entry.value), [entries]);
  const availableOptions = useMemo(
    () => options.filter((option) => !selectedValues.includes(option.value)),
    [options, selectedValues],
  );

  const getOptionLabel = useCallback((option: FAutocompleteOption) => option.label, []);
  const isOptionEqualToValue = useCallback(
    (option: FAutocompleteOption, selected: FAutocompleteOption) => option.value === selected.value,
    [],
  );

  const pendingValue = freeText ? pendingText.trim() : (pendingOption?.value ?? '');

  const handleAdd = useCallback(async () => {
    if (disabled || addInProgress || addingRef.current || !pendingValue) return;
    addingRef.current = true;
    setIsAdding(true);
    try {
      const added = await onAdd(pendingValue);
      if (added === false) return;
      setPendingOption(null);
      setPendingText('');
    } finally {
      addingRef.current = false;
      setIsAdding(false);
    }
  }, [addInProgress, disabled, onAdd, pendingValue]);

  const handleConfirmDelete = useCallback(async () => {
    if (pendingDeleteEntry == null) return;
    if (disabled) return;
    // Redux Toolkit dispatch resolves with a rejected action instead of throwing, so the hook
    // reports success as a boolean — keep the dialog open on failure so it can be retried.
    const deleted = await onDelete(pendingDeleteEntry.id);
    if (deleted) {
      setPendingDeleteEntry(null);
    }
  }, [disabled, onDelete, pendingDeleteEntry]);

  const addField = freeText ? (
    <FInput
      disabled={disabled || addInProgress}
      error={error}
      fullWidth
      label={label}
      onChange={(event) => setPendingText(event.target.value)}
      placeholder={placeholder}
      required={required}
      value={pendingText}
    />
  ) : (
    <FAutocomplete
      disabled={disabled || addInProgress || availableOptions.length === 0}
      error={error}
      fullWidth
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      label={label}
      onChange={(_event, option) => setPendingOption(option)}
      options={availableOptions}
      placeholder={placeholder}
      required={required}
      value={pendingOption}
    />
  );

  const addButtonStack = (
    <Stack direction="row" justifyContent="flex-end" alignItems="center">
      <FHButton
        colorTheme={Themes.Primary}
        disabled={disabled || addInProgress || !pendingValue}
        onClick={handleAdd}
        startIcon={
          error ? (
            <ErrorIcon sx={{ color: FORTA_ERROR }} />
          ) : (
            <AddIcon sx={{ color: FORTA_WHITE }} />
          )
        }
        sx={[
          ...(addButtonSx == null ? [] : Array.isArray(addButtonSx) ? addButtonSx : [addButtonSx]),
          ...(error ? [SELECTION_LIST_ERROR_BUTTON_SX] : []),
        ]}
      >
        {addButtonLabel}
      </FHButton>
    </Stack>
  );

  return (
    <FBoxContainer sx={LIST_CONTAINER_SX}>
      {isSaving ? <FProgressBar /> : null}

      {!isMobile && !readOnlyFromContext && <Box mb={2}>{addButtonStack}</Box>}

      {readOnlyFromContext ? null : addField}

      {isMobile && !readOnlyFromContext && <Box mt={2}>{addButtonStack}</Box>}

      {entries.length > 0 ? (
        <Stack mt={readOnlyFromContext ? 0 : 2}>
          {entries.map((entry, index) => {
            const option = optionsByValue.get(entry.value);
            return (
              <Stack
                key={entry.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={[
                  { px: 2, py: 1 },
                  LIST_ROW_BORDER_SX,
                  LIST_ROW_BG_SX[index % 2],
                ]}
              >
                <Typography sx={{ color: FORTA_BLUE.dark }}>
                  {option?.label ?? entry.value}
                </Typography>
                {readOnlyFromContext ? null : (
                  <IconButton
                    aria-label={`Delete ${option?.label ?? entry.value}`}
                    disabled={disabled || isSaving}
                    onClick={() => setPendingDeleteEntry(entry)}
                    size="small"
                    sx={{ p: 0 }}
                  >
                    <DeleteIconBordered width={36} height={36} />
                  </IconButton>
                )}
              </Stack>
            );
          })}
        </Stack>
      ) : null}

      <ConfirmDialog
        open={pendingDeleteEntry != null}
        title={pendingDeleteEntry ? getDeleteConfirm(pendingDeleteEntry).title : ''}
        message={pendingDeleteEntry ? getDeleteConfirm(pendingDeleteEntry).message : ''}
        onClose={() => setPendingDeleteEntry(null)}
        onConfirm={handleConfirmDelete}
        confirmLoading={isSaving}
      />
    </FBoxContainer>
  );
}
